import Actions from "./actions";

/**
 * Server-driven typing indicators ("Anika is typing…") are leases, not toggles.
 *
 * The server sends an ON carrying `ttlMs`, and later an OFF. But the OFF is a
 * fire-and-forget socket event, and this tab misses it whenever it isn't
 * listening at that moment: the user is on another route, the socket is
 * reconnecting, the tab is frozen, or the server process that owed the OFF has
 * exited. The ON, meanwhile, lives on in the store (`chat.isTypingData`), and
 * nothing else ever cleared it — so one missed OFF pinned "Anika is typing…"
 * on screen until a reload. It happened in production when a student left the
 * chat for ~90 seconds while Anika's reply was being suppressed.
 *
 * So a leased ON expires by itself after `ttlMs`, and a message from the
 * typist in that room ends it at once. The expiry timer lives here, in the
 * socket listener's thunk rather than in a component, so it keeps running
 * while the chat page is unmounted.
 *
 * Only events that carry `ttlMs` are leased. Human typing is focus-driven —
 * ON on focus, OFF on blur, no `ttlMs` — and keeps exactly its existing
 * behaviour.
 */

/** Upper bound on a server-supplied lease, so a bad value can't pin it again. */
export const MAX_TYPING_TTL_MS = 120000;

let leaseSeq = 0;

/**
 * Lease length for a typing event, or null when the event isn't leased
 * (an OFF, or human typing that carries no `ttlMs`).
 */
export const typingLeaseMs = (data) => {
  if (!data?.typing) return null;
  const ttl = Number(data?.ttlMs);
  if (!Number.isFinite(ttl) || ttl <= 0) return null;
  return Math.min(ttl, MAX_TYPING_TTL_MS);
};

/** Clear the typing slot only if it still holds what `isStale` recognises. */
const clearTypingIf = (isStale) => (dispatch, getState) => {
  if (isStale(getState().chat?.isTypingData || {})) {
    dispatch(Actions.setSystemTypingData({}));
  }
};

/** Socket `typing` event → store, arming an expiry for leased events. */
export const receiveTyping = (data) => (dispatch) => {
  const ttl = typingLeaseMs(data);
  if (!ttl) {
    dispatch(Actions.setSystemTypingData(data));
    return;
  }
  const leaseId = ++leaseSeq;
  dispatch(Actions.setSystemTypingData({ ...data, leaseId }));
  // Expire only the lease armed here. If a newer event has taken the slot
  // since, it owns it and this timer does nothing.
  setTimeout(
    () => dispatch(clearTypingIf((typing) => typing.leaseId === leaseId)),
    ttl
  );
};

/**
 * A message from the typist in that room ends their leased indicator — the
 * reply they were "typing" has arrived. Human (unleased) typing is left alone.
 */
export const endTypingOnMessage = (roomId, message) =>
  clearTypingIf(
    (typing) =>
      Boolean(typing.leaseId) &&
      typing.roomName === roomId &&
      Boolean(message?.author) &&
      typing.userName === message.author
  );
